/**
 * The three areas of a team-games room, and who is standing in each.
 *
 * A team is capped and the hall is not, so the map is the one place that can
 * answer "may this person join". The rule is applied where a move lands rather
 * than where it is sent: a client that has not yet heard about the fifth person
 * would otherwise let a sixth in, and both of them would believe it.
 *
 * Absence means the hall. An arrival costs no message that way, an empty map is
 * a room where everybody is standing together, and a snapshot published by a
 * client that predates the areas is a room in the hall rather than no room.
 *
 * Pure state only — no transport, no React. Where the seating is carried is
 * `roomControl.ts`, and what it sounds like is `useSpatialMix.ts`.
 */

import { z } from "zod";

export const RoomAreaSchema = z.enum(["team-1", "hall", "team-2"]);
export type RoomArea = z.infer<typeof RoomAreaSchema>;

/** Where everybody starts, and where absence from the map means. */
export const DEFAULT_ROOM_AREA: RoomArea = "hall";

/** Left to right, which is also the order the panels are drawn in. */
export const ROOM_AREAS: readonly RoomArea[] = ["team-1", "hall", "team-2"];

/**
 * How many a team holds. Five, once: the issue asks for five and rules out
 * configuring it, so there is nothing to thread and nowhere for a second number
 * to drift to. The room's own cap is `MAX_PARTICIPANTS`, and this is a cap
 * inside it.
 */
export const TEAM_CAPACITY = 5;

/** peerId -> area. A peer standing in the hall need not appear. */
export type AreaMap = Record<string, RoomArea>;

/** True for the two areas that are capped. */
export function isTeam(area: RoomArea): boolean {
  return area !== "hall";
}

/** Where this peer is standing, hall for anybody the map does not name. */
export function areaOf(areas: AreaMap, peerId: string): RoomArea {
  return areas[peerId] ?? DEFAULT_ROOM_AREA;
}

/**
 * Everybody standing in one area, out of the roster rather than out of the map:
 * the hall is the peers the map is silent about, so it can only be counted
 * against the people who are actually here.
 */
export function membersOf(
  areas: AreaMap,
  peerIds: readonly string[],
  area: RoomArea,
): string[] {
  return peerIds.filter((peerId) => areaOf(areas, peerId) === area);
}

/** How many are standing in an area: the people who are here, counted. */
export function countOf(
  areas: AreaMap,
  peerIds: readonly string[],
  area: RoomArea,
): number {
  return membersOf(areas, peerIds, area).length;
}

/**
 * True while the area is a team with no place left in it.
 *
 * Counted from the map rather than from the roster, unlike `countOf`: the cap
 * has to hold on a client whose roster is one presence sweep behind, or a
 * client that has not yet been told who the fifth person is would open a sixth
 * place. What is shown on screen is what can be seen; what is refused is what
 * the room has agreed.
 */
export function isFull(areas: AreaMap, area: RoomArea): boolean {
  if (!isTeam(area)) return false;
  return (
    Object.values(areas).filter((held) => held === area).length >= TEAM_CAPACITY
  );
}

/**
 * The seating after a peer asks to stand somewhere, or the seating it was given
 * when the answer is no.
 *
 * Returning the same object for a refusal is what lets the reducer above tell
 * an accepted move from a refused one without a second rule.
 */
export function applyAreaMove(
  areas: AreaMap,
  peerId: string,
  area: RoomArea,
): AreaMap {
  if (areaOf(areas, peerId) === area) return areas;
  if (isTeam(area) && isFull(areas, area)) return areas;
  const next = { ...areas };
  // The hall is absence, so standing in it is an entry removed rather than
  // written: two spellings of the hall would count differently.
  if (area === DEFAULT_ROOM_AREA) delete next[peerId];
  else next[peerId] = area;
  return next;
}

/** Forgets where a departing peer stood, freeing the place. */
export function forgetArea(areas: AreaMap, peerId: string): AreaMap {
  if (!(peerId in areas)) return areas;
  const next = { ...areas };
  delete next[peerId];
  return next;
}

/**
 * Why this peer is not standing where they asked to, or null while there is
 * nothing to say.
 *
 * Derived rather than messaged: the state a client is holding already knows
 * that the asker is elsewhere and that the team is full, so no refusal has to
 * be addressed to a particular peer and carried over the wire. It answers the
 * ordinary case — a team that was already full when the button was pressed —
 * with no round trip, and a lost race as soon as the host's snapshot lands.
 */
export function refusalFor(
  areas: AreaMap,
  peerId: string,
  requested: RoomArea | null,
): "full" | null {
  if (!requested || !isTeam(requested)) return null;
  if (areaOf(areas, peerId) === requested) return null;
  return isFull(areas, requested) ? "full" : null;
}
